import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Share, MoreHorizontal, PlusSquare, Copy, Check, ArrowDown, Compass } from "lucide-react";

export function detectIOSBrowser() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/FBAN|FBAV|Instagram|WhatsApp|Line\/|Telegram|Snapchat|TikTok|GSA\//i.test(ua)) return "inapp";
  if (/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return "other";
  return "safari";
}

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3" data-testid={`ios-guide-step-${n}`}>
    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#014e86] text-sm font-black text-white">{n}</span>
    <div className="flex-1 pt-1 text-[15px] leading-snug text-stone-800">
      {Icon && <Icon className="mr-1 inline h-5 w-5 align-[-4px] text-[#007aff]" strokeWidth={2.5} />}
      {children}
    </div>
  </li>
);

const InAppSteps = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
    } catch {}
  };
  return (
    <>
      <p className="text-[15px] leading-snug text-stone-700">
        Stai usando il browser interno di un'altra app: da qui non si può aggiungere Wall Week alla Home.
      </p>
      <ol className="space-y-4">
        <Step n={1} icon={Copy}>Copia il link qui sotto</Step>
        <Step n={2} icon={Compass}>Apri <b>Safari</b> e incollalo nella barra degli indirizzi</Step>
        <Step n={3}>Tocca di nuovo "Installa Wall Week" e segui la guida</Step>
      </ol>
      <button
        type="button"
        onClick={copy}
        data-testid="ios-guide-copy-link"
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#014e86] py-3 text-sm font-bold text-white active:scale-[0.98] transition-transform"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copied ? "Link copiato!" : "Copia link"}
      </button>
    </>
  );
};

const SafariSteps = ({ browser }) => (
  <ol className="space-y-4">
    <Step n={1} icon={browser === "safari" ? MoreHorizontal : Share}>
      {browser === "safari" ? (
        <>Tocca <b>•••</b> in basso (oppure l'icona <Share className="inline h-4 w-4 align-[-2px] text-[#007aff]" /> Condividi)</>
      ) : (
        <>Tocca l'icona <b>Condividi</b> nella barra del browser</>
      )}
    </Step>
    <Step n={2} icon={Share}>Se serve, tocca <b>Condividi</b>, poi scorri in basso</Step>
    <Step n={3} icon={PlusSquare}>Scegli <b>"Aggiungi alla schermata Home"</b> e conferma con <b>Aggiungi</b></Step>
  </ol>
);

export default function IOSInstallGuide({ onClose }) {
  const browser = detectIOSBrowser();
  return createPortal(
    <div className="fixed inset-0 flex items-end justify-center" style={{ zIndex: 2147483000 }} data-testid="ios-install-guide">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-t-3xl bg-white px-5 pt-5 shadow-2xl animate-in slide-in-from-bottom" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
        <button type="button" onClick={onClose} aria-label="Chiudi" data-testid="ios-guide-close" className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-stone-100">
          <X className="h-4 w-4" strokeWidth={2.5} />
        </button>
        <div className="mb-4 flex items-center gap-3">
          <img src="/wallweek-logo-192.png" alt="Wall Week" className="h-12 w-12 rounded-xl" />
          <div>
            <h3 className="text-lg font-black text-stone-900">Installa Wall Week</h3>
            <p className="text-xs text-stone-500">Su iPhone si fa in 3 passi</p>
          </div>
        </div>
        <div className="space-y-4">
          {browser === "inapp" ? <InAppSteps /> : <SafariSteps browser={browser} />}
        </div>
        {browser !== "inapp" && (
          <div className="mt-5 flex flex-col items-center text-[#007aff]" data-testid="ios-guide-arrow">
            <span className="text-xs font-bold">I tasti sono nella barra qui sotto</span>
            <ArrowDown className="h-7 w-7 animate-bounce" strokeWidth={3} />
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
